import { createContext, useCallback, useContext } from 'react';
import type { Hints, ScreenWords } from './api';

/**
 * СЛОВА ЭКРАНОВ «КЛИЕНТКИ» — словарь и три способа его спросить (`s206`).
 *
 * Компоненты, которые его рисуют, живут в `words.tsx`: правило быстрой
 * перезагрузки Vite не терпит файла, где рядом с компонентами лежат функции.
 *
 * ⛔ НИ ОДНОЙ ФРАЗЫ ВНУТРИ ЭТОГО ФАЙЛА, И ЭТО ГЛАВНОЕ ЕГО СВОЙСТВО. Подпись
 * поля, текст подсказки и честная пустота приезжают из `ui_hints` и `ui_terms`
 * (operational-rules §31.1). Впиши сюда хоть одно «Телефон не указан» — и его
 * правка потребовала бы сборки фронта, а соседние строки выглядели бы точно
 * так же: отличить правимое от вшитого стало бы нельзя.
 *
 * ⛔ НЕТ СТРОКИ В СЛОВАРЕ — ВИДЕН КЛЮЧ, А НЕ ПУСТОТА. Пустое место читается
 * как «здесь ничего и не должно быть», и забытая строка живёт годами. Ключ на
 * экране некрасив ровно настолько, чтобы его заметили и завели слово (закон
 * `s204`: неизвестный код показывает СЕБЯ).
 *
 * ⛔ ЗНАЧОК «?» ЖИВЁТ ТОЛЬКО ПРИ ТЕКСТЕ. Значок с пустым облачком выглядит
 * поломкой; отсутствие значка честно значит «объяснения пока нет» (закон
 * `s105`, `Hint`).
 */

export interface Dictionary {
  hints: Hints;
  words: ScreenWords;
}

export const WordsContext = createContext<Dictionary>({ hints: {}, words: { labels: {}, texts: {} } });

/** Подпись поля: `status` → «Лид или клиентка». */
export function useLabel(): (key: string) => string {
  const { words } = useContext(WordsContext);

  return useCallback((key: string) => words.labels[key] ?? key, [words]);
}

/** Текст подсказки «?» или `null`, если слова ещё нет. */
export function useHintText(): (key: string) => string | null {
  const { hints } = useContext(WordsContext);

  return useCallback((key: string) => hints[key] ?? null, [hints]);
}

/**
 * Честная пустота, отказ, ожидание или результат: `future.none` →
 * «Следующей записи нет.». Подстановки макета (`{где}`, `{имя}`, `{число}`)
 * заменяются переданными значениями.
 */
export function useText(): (key: string, vars?: Record<string, string | number>) => string {
  const { words } = useContext(WordsContext);

  return useCallback(
    (key: string, vars?: Record<string, string | number>) => {
      const raw = words.texts[key];

      if (raw === undefined) {
        return key;
      }

      if (!vars) {
        return raw;
      }

      return Object.entries(vars).reduce(
        (acc, [name, value]) => acc.split(`{${name}}`).join(String(value)),
        raw,
      );
    },
    [words],
  );
}
